import Link from "@mui/material/Link";
import {
  BooleanField,
  FunctionField,
  Identifier,
  NumberField,
  TextField,
  useDataProvider,
  useRecordContext,
} from "react-admin";
import { MutationFunction, MutationKey } from "react-query";

import {
  Action,
  CustomDataProvider,
  DataKind,
  DropboxTokenResponse,
  getRegionalResourceName,
  Region,
  Resource,
  ResourceRegional,
  SyncErrorResponse,
  UserResponse,
} from "../../types";
import {
  ActionMenu,
  Authorize,
  CustomDatagrid,
  CustomDateTimeField,
  CustomReferenceField,
  CustomReferenceManyField,
  CustomShow,
  MutationActionMenuItem,
  ProjectsForSync,
  ShowTable,
} from "../custom";

interface DisconnectProjectsMutationFnVariables {
  id: Identifier;
}

interface ResetCursorMutationFnVariables {
  id: Identifier;
}

interface RevokeTokenMutationFnVariables {
  id: Identifier;
}

function DisconnectProjects(props: { region: Region }) {
  const record = useRecordContext<DropboxTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: DropboxTokenResponse },
      DisconnectProjectsMutationFnVariables
    > = (variables: DisconnectProjectsMutationFnVariables) => {
      return dataProvider.disconnectProjectsForDropboxToken(
        props.region,
        variables.id,
      );
    };
    const mutationKey: MutationKey = [
      "disconnectProjectsForDropboxToken",
      record.id,
    ];
    const successMessage = `Disconnected all projects connected to the Dropbox token: ${record.id}. ✅`;

    return (
      <MutationActionMenuItem
        label="Disconnect Projects"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function ResetCursor(props: { region: Region }) {
  const record = useRecordContext<DropboxTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: DropboxTokenResponse },
      ResetCursorMutationFnVariables
    > = (variables: ResetCursorMutationFnVariables) => {
      return dataProvider.resetCursor(props.region, variables.id);
    };
    const mutationKey: MutationKey = ["resetCursor", record.id];
    const successMessage = "Token cursor reset successfully! ✅";

    return (
      <MutationActionMenuItem
        label="Reset Cursor"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function RevokeToken(props: { region: Region }) {
  const record = useRecordContext<DropboxTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: DropboxTokenResponse },
      RevokeTokenMutationFnVariables
    > = (variables: RevokeTokenMutationFnVariables) => {
      return dataProvider.revokeDropboxToken(props.region, variables.id);
    };
    const mutationKey: MutationKey = ["revokeDropboxToken", record.id];
    const successMessage = "Token revoked successfully! ✅";

    return (
      <MutationActionMenuItem
        label="Revoke Token"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function DropboxTokenShow(props: { region: Region }) {
  const fieldwireActions = [
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.DropboxToken,
      element: <DisconnectProjects region={props.region} />,
    },
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.DropboxToken,
      element: <ResetCursor region={props.region} />,
    },
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.DropboxToken,
      element: <RevokeToken region={props.region} />,
    },
  ];

  return (
    <CustomShow
      checkPermissionFor={DataKind.DropboxToken}
      displayDelete={false}
      displayEdit={false}
      fieldwireActions={<ActionMenu fieldwireActions={fieldwireActions} />}
    >
      {/* START OF DROPBOX TOKEN DETAILS */}
      <ShowTable title="Dropbox Token Details">
        <CustomReferenceField
          label="User"
          source="user_id"
          reference={Resource.User}
        >
          <FunctionField
            render={(record: UserResponse) =>
              `${record.first_name} ${record.last_name}`
            }
          />
        </CustomReferenceField>
        <BooleanField label="Is Valid" source="is_valid" />
        <CustomDateTimeField label="Last Pulled At" source="last_pulled_at" />
        <CustomDateTimeField label="Last Pushed At" source="last_pushed_at" />
        <CustomDateTimeField label="Created At" source="created_at" />
        <CustomDateTimeField label="Updated At" source="updated_at" />
        <CustomDateTimeField label="Expires At" source="expires_at" />
        <TextField label="Email" source="email" />
        <TextField label="Cursor" source="cursor" />
      </ShowTable>
      {/* END OF DROPBOX TOKEN DETAILS */}

      {/* START OF PROJECTS */}
      <Authorize
        action={Action.Read}
        dataKind={DataKind.DropboxToken}
        disableUnauthorizedMessage
      >
        <ProjectsForSync
          region={props.region}
          targetRegional={ResourceRegional.DropboxToken}
        />
      </Authorize>
      {/* END OF PROJECTS */}

      {/* START OF SYNC ERRORS */}
      {/* Backend enforces the ordering for sync errors - update_at_desc */}
      <Authorize action={Action.Read} dataKind={DataKind.DropboxToken}>
        <ShowTable rowFlexDirection="column" title="Sync Errors">
          <CustomReferenceManyField
            displayPagination={true}
            label={false}
            reference={getRegionalResourceName(
              props.region,
              ResourceRegional.SyncError,
            )}
            target={getRegionalResourceName(
              props.region,
              ResourceRegional.DropboxToken,
            )}
          >
            <CustomDatagrid>
              <CustomDateTimeField
                label="Last Occurred"
                source="updated_at"
                sortable={false}
              />
              <CustomDateTimeField
                label="First Occurred"
                source="created_at"
                sortable={false}
              />
              <CustomReferenceField
                label="User"
                source="sync_owner_id"
                reference={Resource.User}
                sortable={false}
              >
                <TextField source="email" />
              </CustomReferenceField>
              <NumberField label="Count" source="count" sortable={false} />
              <TextField
                label="Entity Type"
                source="entity_type"
                sortable={false}
              />
              <TextField
                label="Entity Name"
                source="entity_name"
                sortable={false}
              />
              <TextField
                label="Entity Id"
                source="entity_id"
                sortable={false}
              />
              <FunctionField
                label="Error (May Link to Rollbar)"
                render={(record: SyncErrorResponse) =>
                  record.error_url ? (
                    <Link
                      href={record.error_url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {record.error_message}
                    </Link>
                  ) : (
                    record.error_message
                  )
                }
                sortable={false}
              />
            </CustomDatagrid>
          </CustomReferenceManyField>
        </ShowTable>
      </Authorize>
      {/* END OF SYNC ERRORS */}
    </CustomShow>
  );
}

export default DropboxTokenShow;
